import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// --- Saved Wallets ---
export function useSavedWallets() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["saved-wallets", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_wallets")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useSaveWallet() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (wallet: { name: string; address: string; chain: string }) => {
      const { error } = await supabase.from("saved_wallets").insert({
        user_id: user!.id,
        ...wallet,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-wallets"] }),
  });
}

export function useDeleteSavedWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("saved_wallets").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-wallets"] }),
  });
}

export function useRenameWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase.from("saved_wallets").update({ name }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-wallets"] }),
  });
}

// --- Price Alerts ---
export function usePriceAlerts() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["price-alerts", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("price_alerts")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useCreateAlert() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (alert: { token: string; direction: string; target_price: number }) => {
      const { error } = await supabase.from("price_alerts").insert({
        user_id: user!.id,
        ...alert,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["price-alerts"] }),
  });
}

export function useToggleAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("price_alerts").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["price-alerts"] }),
  });
}

export function useDeleteAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("price_alerts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["price-alerts"] }),
  });
}

// --- User Preferences ---
export function useUserPreferences() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["user-preferences", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useUpdatePreferences() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (prefs: { currency?: string; language?: string; notifications_enabled?: boolean }) => {
      if (!user) throw new Error("Not authenticated");

      const { data: existing, error: selectError } = await supabase
        .from("user_preferences")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();
      if (selectError) throw selectError;

      if (existing) {
        const { error } = await supabase.from("user_preferences").update(prefs).eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("user_preferences").insert({ user_id: user.id, ...prefs });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-preferences"] }),
  });
}

// --- Portfolio Snapshots ---
export function usePortfolioSnapshots(days = 90) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["portfolio-snapshots", user?.id, days],
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - days);
      const { data, error } = await supabase
        .from("portfolio_snapshots")
        .select("*")
        .gte("snapshot_date", since.toISOString().split("T")[0])
        .order("snapshot_date", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useSaveSnapshot() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (snapshot: { total_balance_usd: number; wallet_data?: unknown[] }) => {
      const { error } = await supabase.from("portfolio_snapshots").upsert(
        {
          user_id: user!.id,
          total_balance_usd: snapshot.total_balance_usd,
          wallet_data: snapshot.wallet_data as any,
          snapshot_date: new Date().toISOString().split("T")[0],
        },
        { onConflict: "user_id,snapshot_date" }
      );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portfolio-snapshots"] }),
  });
}

// --- Shared Portfolios ---
export function useSharedPortfolio() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["shared-portfolio", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shared_portfolios")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useCreateShareLink() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (displayName?: string) => {
      const { data, error } = await supabase.from("shared_portfolios").insert({
        user_id: user!.id,
        display_name: displayName || user!.email?.split("@")[0] || "CryptoVault User",
      }).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shared-portfolio"] }),
  });
}

export function useToggleShareLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("shared_portfolios").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shared-portfolio"] }),
  });
}
